<script setup lang="ts">
/**
 * Gamification onboarding tour trigger (M4C-27).
 *
 * Assembles the five-step tour and decides WHEN it appears. Mounted once in the
 * member layout; the shell teleports its overlay to <body>, so placement is
 * incidental. Skip and the final Continue both dismiss and mark the tour seen
 * (the shell self-dismisses on those events; this only records "seen").
 *
 * Auto-trigger (dev/QA): show the tour once per auth session for any logged-in
 * member, resetting on logout so it re-appears on every fresh login for
 * inspection. Flip `REQUIRE_ONBOARDING_COMPLETE` to restore the production gate
 * (show once after the member finishes profile onboarding); persistence then
 * moves from sessionStorage to a real `profile.tour_seen` API flag (see the tour
 * store + M4C-25). The "How it works" reopen entry point lands with its host
 * view (M4C-20) — it just calls `useTourStore().open()`.
 */
import OnboardingTourShell from '~/components/onboarding/tour/Shell.vue';
import Step1Welcome from '~/components/onboarding/tour/steps/Step1Welcome.vue';
import Step2Sky from '~/components/onboarding/tour/steps/Step2Sky.vue';
import Step3Fragments from '~/components/onboarding/tour/steps/Step3Fragments.vue';
import Step4Progression from '~/components/onboarding/tour/steps/Step4Progression.vue';
import Step5Sky from '~/components/onboarding/tour/steps/Step5Sky.vue';
import type { TourStep } from '~/components/onboarding/tour/types';

/** Set true for production: only auto-show after profile onboarding completes. */
const REQUIRE_ONBOARDING_COMPLETE = false;

const tour = useTourStore();
const auth = useAuthStore();

const steps: TourStep[] = [
  { component: Step1Welcome },
  { component: Step2Sky },
  { component: Step3Fragments },
  { component: Step4Progression },
  { component: Step5Sky },
];

function passesGate(): boolean {
  if (!auth.logged) {
    return false;
  }
  if (REQUIRE_ONBOARDING_COMPLETE) {
    return auth.user?.profile?.onboarding_complete === true;
  }
  return true;
}

function evaluate(): void {
  if (passesGate()) {
    tour.autoStart();
  }
}

// Re-evaluate on login; clear the per-session "seen" flag on logout so the next
// login shows the tour again.
watch(
  () => auth.logged,
  (loggedIn) => {
    if (loggedIn) {
      evaluate();
    } else {
      tour.resetSeen();
    }
  },
);

onMounted(evaluate);

function onDismiss(): void {
  tour.markSeen();
}
</script>

<template>
  <OnboardingTourShell
    v-model:visible="tour.visible"
    :steps="steps"
    @skip="onDismiss"
    @finish="onDismiss"
  />
</template>
